import { useCallback, useRef } from 'react';

const COLOURS = [
  '#e63946', '#457b9d', '#2a9d8f', '#e9c46a', '#f4a261',
  '#a8dadc', '#f72585', '#4cc9f0', '#7209b7', '#06d6a0',
  '#ffd166', '#ef476f', '#118ab2', '#073b4c', '#ff6b6b',
];

const SHAPES = ['rect', 'circle'];

export function useConfetti() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  const fireConfetti = useCallback(() => {
    // Remove any existing canvas
    if (canvasRef.current) {
      canvasRef.current.remove();
      canvasRef.current = null;
    }
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }

    // Create full-screen canvas overlay
    const canvas = document.createElement('canvas');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'pointer-events:none',
      'z-index:99999',
    ].join(';');
    document.body.appendChild(canvas);
    canvasRef.current = canvas;

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    // Spawn 220 particles from random top positions for a shower effect
    particlesRef.current = Array.from({ length: 220 }, () => {
      const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      return {
        x: Math.random() * W,
        y: Math.random() * H * -0.3 - 10,   // start above viewport
        w: Math.random() * 10 + 6,
        h: Math.random() * 6 + 4,
        colour: COLOURS[Math.floor(Math.random() * COLOURS.length)],
        shape,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 4 + 3,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.3,
        opacity: 1,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: Math.random() * 0.08 + 0.03,
      };
    });

    let startTime = null;
    const DURATION = 3200; // ms

    const draw = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / DURATION, 1);

      ctx.clearRect(0, 0, W, H);

      particlesRef.current.forEach((p) => {
        // Physics
        p.wobble += p.wobbleSpeed;
        p.x += p.vx + Math.sin(p.wobble) * 1.5;
        p.y += p.vy;
        p.angle += p.spin;
        p.vy += 0.08; // gravity

        // Fade out in last 40% of animation
        p.opacity = progress < 0.6 ? 1 : 1 - ((progress - 0.6) / 0.4);

        ctx.save();
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.colour;

        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.ellipse(0, 0, p.w / 2, p.h / 2, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }

        ctx.restore();
      });

      if (progress < 1) {
        animRef.current = requestAnimationFrame(draw);
      } else {
        // Clean up
        canvas.remove();
        canvasRef.current = null;
        particlesRef.current = [];
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(draw);
  }, []);

  return fireConfetti;
}
